import { useState, type FormEvent } from "react";

import type {
  AdminRole,
  AdminUser,
  Customer,
  InviteAdminUserResponse
} from "@attendik/types/admin-schemas";

import { api } from "../api/client.js";
import { isApiError } from "../api/errors.js";
import { PageHeader } from "../components/AppLayout.js";
import { Button } from "../components/ui/button.js";
import { Card } from "../components/ui/card.js";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader
} from "../components/ui/dialog.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { Select } from "../components/ui/select.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../components/ui/table.js";
import { EmptyState, ErrorState, LoadingState } from "../components/states.js";
import { useAsync } from "../lib/useAsync.js";
import { useAuth, useScope } from "../store/auth.js";
import { ResetPasswordDialog } from "./OperatorsPage.js";
import { StatusBadge, accountStatusVariant } from "./shared/pills.js";

interface AdminUsersData {
  users: AdminUser[];
  customers: Customer[];
}

/** Admin users page — list, invite, disable/enable, reset password (P2-T10). */
export function AdminUsersPage(): JSX.Element {
  const { isPortalAdmin, customerId } = useScope();
  const { user: currentUser } = useAuth();
  const [inviting, setInviting] = useState(false);
  /** "Invite by email" — server generates a temp password and emails it. */
  const [invitingByEmail, setInvitingByEmail] = useState(false);
  const [resetting, setResetting] = useState<AdminUser | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  /** Top-of-page toast for invite outcomes — non-blocking, clears on close. */
  const [toast, setToast] = useState<{
    kind: "success" | "warning";
    message: string;
  } | null>(null);

  const { data, loading, error, reload } = useAsync<AdminUsersData>(async () => {
    const users = await api.listAdminUsers();
    const customers = isPortalAdmin ? await api.listCustomers() : [];
    return { users, customers };
  }, [isPortalAdmin]);

  const customerLabel = (id: string | null): string => {
    if (!id) {
      return "Portal";
    }
    return data?.customers.find((c) => c.id === id)?.name ?? "—";
  };

  const toggleStatus = async (adminUser: AdminUser): Promise<void> => {
    setActionError(null);
    try {
      await api.updateAdminUser(adminUser.id, {
        status: adminUser.status === "ACTIVE" ? "DISABLED" : "ACTIVE"
      });
      reload();
    } catch (err) {
      setActionError(isApiError(err) ? err.message : "Could not update the user.");
    }
  };

  return (
    <>
      <PageHeader
        title="Admin users"
        description={
          isPortalAdmin
            ? "All admin users across the platform."
            : "Admin users for your customer."
        }
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setInviting(true)}>
              Create user
            </Button>
            <Button onClick={() => setInvitingByEmail(true)}>
              Invite by email
            </Button>
          </div>
        }
      />

      {toast ? (
        <p
          role="status"
          className={
            "mb-3 text-sm " +
            (toast.kind === "success"
              ? "text-emerald-700"
              : "text-amber-700")
          }
        >
          {toast.message}
        </p>
      ) : null}
      {actionError ? (
        <p role="alert" className="mb-3 text-sm text-destructive">
          {actionError}
        </p>
      ) : null}

      <Card>
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState message={error} onRetry={reload} />
        ) : !data || data.users.length === 0 ? (
          <EmptyState title="No admin users" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                {isPortalAdmin ? <TableHead>Customer</TableHead> : null}
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.users.map((adminUser) => {
                const isSelf = adminUser.id === currentUser?.id;
                return (
                  <TableRow key={adminUser.id}>
                    <TableCell className="font-medium">
                      {adminUser.name}
                      {isSelf ? (
                        <span className="ml-2 text-xs text-muted-foreground">
                          (you)
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {adminUser.email}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {adminUser.role === "portal_admin"
                        ? "Portal admin"
                        : "Customer admin"}
                    </TableCell>
                    {isPortalAdmin ? (
                      <TableCell className="text-muted-foreground">
                        {customerLabel(adminUser.customer_id)}
                      </TableCell>
                    ) : null}
                    <TableCell>
                      <StatusBadge variant={accountStatusVariant(adminUser.status)}>
                        {adminUser.status}
                      </StatusBadge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setResetting(adminUser)}
                        >
                          Reset password
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isSelf}
                          onClick={() => toggleStatus(adminUser)}
                        >
                          {adminUser.status === "ACTIVE" ? "Disable" : "Enable"}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      {inviting ? (
        <InviteAdminUserDialog
          customers={data?.customers ?? []}
          isPortalAdmin={isPortalAdmin}
          ownCustomerId={customerId}
          onClose={() => setInviting(false)}
          onInvited={() => {
            setInviting(false);
            reload();
          }}
        />
      ) : null}

      {invitingByEmail ? (
        <InviteByEmailDialog
          customers={data?.customers ?? []}
          isPortalAdmin={isPortalAdmin}
          ownCustomerId={customerId}
          onClose={() => setInvitingByEmail(false)}
          onInvited={(result) => {
            setInvitingByEmail(false);
            if (result.email_warning) {
              setToast({
                kind: "warning",
                message: `User created, but the invitation email failed to send — re-send later. (${result.email_warning})`
              });
            } else {
              setToast({
                kind: "success",
                message: `Invitation email sent to ${result.email}`
              });
            }
            reload();
          }}
        />
      ) : null}

      {resetting ? (
        <ResetPasswordDialog
          subjectName={resetting.name}
          onClose={() => setResetting(null)}
          onSubmit={(pw) => api.resetAdminUserPassword(resetting.id, pw)}
          onDone={() => setResetting(null)}
        />
      ) : null}
    </>
  );
}

function InviteAdminUserDialog({
  customers,
  isPortalAdmin,
  ownCustomerId,
  onClose,
  onInvited
}: {
  customers: Customer[];
  isPortalAdmin: boolean;
  ownCustomerId: string | null;
  onClose: () => void;
  onInvited: () => void;
}): JSX.Element {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // A customer_admin may only create customer_admin users.
  const [role, setRole] = useState<AdminRole>("customer_admin");
  const [selectedCustomer, setSelectedCustomer] = useState(
    isPortalAdmin ? (customers[0]?.id ?? "") : (ownCustomerId ?? "")
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (password.length < 12) {
      setError("Password must be at least 12 characters.");
      return;
    }
    const customerForUser =
      role === "portal_admin"
        ? null
        : isPortalAdmin
          ? selectedCustomer
          : ownCustomerId;
    if (role === "customer_admin" && !customerForUser) {
      setError("A customer is required for a customer admin.");
      return;
    }
    setSubmitting(true);
    try {
      await api.createAdminUser({
        email: email.trim(),
        name: name.trim(),
        role,
        customer_id: customerForUser,
        password
      });
      onInvited();
    } catch (err) {
      if (isApiError(err) && err.code === "EMAIL_TAKEN") {
        setError("An admin with that email already exists.");
      } else {
        setError(isApiError(err) ? err.message : "Could not invite the admin.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={onClose} labelledBy="invite-admin-user-title">
      <DialogHeader
        id="invite-admin-user-title"
        title="Invite admin user"
        onClose={onClose}
      />
      <form onSubmit={handleSubmit}>
        <DialogBody>
          {isPortalAdmin ? (
            <div className="space-y-1.5">
              <Label htmlFor="au-role">Role</Label>
              <Select
                id="au-role"
                value={role}
                onChange={(e) => setRole(e.target.value as AdminRole)}
              >
                <option value="customer_admin">Customer admin</option>
                <option value="portal_admin">Portal admin</option>
              </Select>
            </div>
          ) : null}
          {role === "customer_admin" && isPortalAdmin ? (
            <div className="space-y-1.5">
              <Label htmlFor="au-customer">Customer</Label>
              <Select
                id="au-customer"
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
                required
              >
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}
          <div className="space-y-1.5">
            <Label htmlFor="au-name">Name</Label>
            <Input
              id="au-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="au-email">Email</Label>
            <Input
              id="au-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="au-password">Initial password</Label>
            <Input
              id="au-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <p className="text-xs text-muted-foreground">Minimum 12 characters.</p>
          </div>
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? "Inviting…" : "Invite"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

/**
 * Invite-by-email dialog — collects email, name, role, and (for portal_admin)
 * customer. Submits to the server, which generates a temp password, persists
 * the user, and emails the credentials.
 */
function InviteByEmailDialog({
  customers,
  isPortalAdmin,
  ownCustomerId,
  onClose,
  onInvited
}: {
  customers: Customer[];
  isPortalAdmin: boolean;
  ownCustomerId: string | null;
  onClose: () => void;
  onInvited: (result: InviteAdminUserResponse) => void;
}): JSX.Element {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  // A customer_admin may only invite customer_admin users.
  const [role, setRole] = useState<AdminRole>("customer_admin");
  const [selectedCustomer, setSelectedCustomer] = useState(
    isPortalAdmin ? (customers[0]?.id ?? "") : (ownCustomerId ?? "")
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    const customerForUser =
      role === "portal_admin"
        ? null
        : isPortalAdmin
          ? selectedCustomer
          : ownCustomerId;
    if (role === "customer_admin" && !customerForUser) {
      setError("A customer is required for a customer admin.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await api.inviteAdminUser({
        email: email.trim(),
        name: name.trim(),
        role,
        customer_id: customerForUser
      });
      onInvited(result);
    } catch (err) {
      if (isApiError(err) && err.code === "EMAIL_TAKEN") {
        setError("An admin with that email already exists.");
      } else {
        setError(isApiError(err) ? err.message : "Could not send the invitation.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={onClose} labelledBy="invite-by-email-title">
      <DialogHeader
        id="invite-by-email-title"
        title="Invite by email"
        onClose={onClose}
      />
      <form onSubmit={handleSubmit}>
        <DialogBody>
          <p className="text-sm text-muted-foreground">
            We&rsquo;ll generate a strong temporary password and email it to
            them. They can change it after signing in.
          </p>
          {isPortalAdmin ? (
            <div className="space-y-1.5">
              <Label htmlFor="ibe-role">Role</Label>
              <Select
                id="ibe-role"
                value={role}
                onChange={(e) => setRole(e.target.value as AdminRole)}
              >
                <option value="customer_admin">Customer admin</option>
                <option value="portal_admin">Portal admin</option>
              </Select>
            </div>
          ) : null}
          {role === "customer_admin" && isPortalAdmin ? (
            <div className="space-y-1.5">
              <Label htmlFor="ibe-customer">Customer</Label>
              <Select
                id="ibe-customer"
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
                required
              >
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}
          <div className="space-y-1.5">
            <Label htmlFor="ibe-name">Name</Label>
            <Input
              id="ibe-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ibe-email">Email</Label>
            <Input
              id="ibe-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? "Sending…" : "Send invitation"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
